<style lang="scss" scoped>
.schedule-card {
    position: relative;
    border-radius: 2px;
    border: 1px solid var(--border-color);
    background-color: rgba(255, 255, 255, 0.9);
    height: 100%;
    width: calc((100% - (7 * 16px)) / 7);
    padding: 24px 16px;
    margin: 8px;
    display: flex;
    flex-wrap: wrap;
    flex-flow: column;
    @media screen and (max-height: 1024px) {
        padding: 16px 8px;
    }
    &.after {
        background-color: #f4f6f8;
        .item {
            opacity: 0.3;
        }
    }
    .i-42 {
        @media screen and (max-width: 1366px) {
            width: 30px;
        }
    }
    .date {
        position: absolute;
        top: -50px;
        white-space: pre;
        &:not(.left) {
            right: -40px;
        }
        &.left {
            left: -40px;
            opacity: 0.9;
        }
    }
    .item-text {
        font-size: 0.8rem;
    }
    &:after {
        content: "";
        height: calc(100% + 100px);
        width: 1px;
        background: var(--border-color);
        position: absolute;
        top: -50px;
        left: 0;
        right: 0;
        margin: auto;
        z-index: -1;
    }

    .btn-primary-dark {
        &.urgent {
            display: flex;
            background: var(--danger);
            justify-content: inherit;
            &:hover {
                border: 1px solid var(--danger);
                background: transparent;
                color: var(--danger);
                .urgent-marker {
                    border: 1px solid var(--danger);
                    color: var(--danger);
                }
            }
            .urgent-marker {
                color: #fff;
                border-radius: 50%;
                border: 1px solid #fff;
                height: 12px;
                width: 12px;
                display: block;
                font-size: 9px;
                margin: auto 5px auto 0;
                line-height: 10px;
            }
        }
    }
}
.mobile-schedule-card {
    background-color: rgba(255, 255, 255, 0.9);
    border-left: 1px solid var(--border-color);
    border-right: 1px solid var(--border-color);
    &.after {
        background-color: #f4f6f8;
        .item {
            opacity: 0.3;
        }
    }
}
</style>

<template>

    <div v-if="$root.sizer === 'lg'" class="schedule-card" :class="{ 'after' : (isAfter || locked) && !completed, 'yesterday' : isYesterday || completed }">

        <!-- DATE -->
        <div v-if="week === 1 && day === 0" class="date text-center us-none left">
            <p class="fw-b" v-if="!$moment(date).add(-1, 'day').isSame(this.$moment().startOf('day'), 'd')">{{ $moment(date).add(-1, 'day').format("dddd") }}</p>
            <p class="fw-b text-gold" v-else>{{ $t('pages.schedule.today') }}</p>
            <p class="small">{{ $moment(date).add(-1, 'day').format("D MMMM") }}</p>
        </div>
        <div class="date text-center us-none">
            <p class="fw-b" v-if="!isToday">{{ $moment(date).add(0, 'day').format("dddd") }}</p>
            <p class="fw-b text-gold" v-else>{{ $t('pages.schedule.today') }}</p>
            <p class="small">{{ $moment(date).add(0, 'day').format("D MMMM") }}</p>
        </div>

        <!-- FROM  -->
        <!-- Show the time the user actually went to sleep  -->
        <div class="item d-flex flex-wrap mb-16" :style="{ opacity: data.in_bed ? 1 : 0.3 }">
            <i-to-bed class="i-42 mx-auto mb-xl-8 icon" :class="sleepStatus"></i-to-bed>
            <p class="item-text text-center w-100">
                <template v-if="data.in_bed">{{ [data.in_bed, serverDateFormat ] | moment("HHumm") }}</template>
                <template v-else-if="recommendedInBed">{{ [ recommendedInBed, "HH:mm" ] | moment("HHumm") }}</template>
            </p>
        </div>

        <!-- TO  -->
        <!-- Show the time the user actually woke up  -->
        <!-- This is always the default wakup time  -->
        <div class="item d-flex flex-wrap mb-16" :style="{ opacity: data.out_bed ? 1 : 0.3 }">
            <i-get-out class="i-42 mx-auto mb-xl-8 icon" :class="wakeupStatus"></i-get-out>
            <p class="item-text text-center w-100">
                <template v-if="data.out_bed">{{ [ data.out_bed, serverDateFormat ] | moment("HHumm") }}</template>
                <template v-else-if="recommendedWakup">{{ [ recommendedWakup, "HH:mm" ] | moment("HHumm") }}</template>
            </p>
        </div>

        <template v-if="(!isAfter && !isYesterday && completed) || completed">
            <div class="item d-flex flex-wrap mb-16">
                <i-wakeup class="i-42 mx-auto mb-xl-8 icon"></i-wakeup>
                <p class="small item-text text-center w-100">{{ data.sleep_disturbances.length }} {{ $t('pages.schedule.times') }}</p>
            </div>

            <div class="item d-flex flex-wrap mb-16">
                <i-clock class="i-42 mx-auto mb-xl-8 icon" :class="inBedTimeStatus"></i-clock>
                <p class="item-text text-center w-100">{{  [ tib, "HH:mm" ] | moment("HHumm")}}</p>
                <p class="small text-center w-100">{{ $t('pages.schedule.total_bed_time') }}</p>
            </div>

            <div class="item d-flex flex-wrap mb-16">
                <i-clock class="i-42 mx-auto mb-xl-8 icon" :class="awakeTimeStatus"></i-clock>
                <p class="item-text text-center w-100">{{  [ tst, "HH:mm" ] | moment("HHumm")}}</p>
                <p class="small text-center w-100">{{ $t('pages.schedule.total_sleep_time') }}</p>
            </div>
        </template>

        <!-- CTA section -->
        <div class="cta mt-auto d-flex flex-wrap justify-content-center">
            <template v-if="!disabled">
                <router-link v-if="(!isAfter && !isYesterday && completed && !locked) || completed" :to="{ name: 'schedule-edit' , params: { id: data.id }, query: { week: week, date: data.date }}" class="small text-primary-dark text-center mb-16">
                    {{ $t('pages.schedule.change_schedule') }}
                </router-link>
            </template>
            <router-link :to="{ name: 'schedule-details', query: { date: data.date, week: week } }" v-if="(isYesterday && !locked && !completed ) || (!completed && !isAfter && !locked)" class="btn btn-primary-dark w-100 text-center" :class="{ urgent: disabled }">
                <span v-if="disabled" v-show="showNote" class="urgent-note"></span>
                <span v-if="disabled" class="urgent-marker">!</span>
                {{ $t('pages.schedule.fill_in') }}
            </router-link>
            <router-link :to="{ name: 'tip-detail', params: { id: data.day }, query: { date: data.date } }" v-else-if="completed" class="btn btn-outline-gold w-100 text-center">
                {{ $t('pages.schedule.advice') }}
            </router-link>
        </div>

    </div>

    <div v-else-if="$root.sizer === 'sm' || $root.sizer === 'md'" class="mx-32 h-100">
        <div class="p-24 text-center mobile-schedule-card h-100 oy-sm-auto" :class="{ 'after' : (isAfter || locked) && !completed, 'yesterday' : isYesterday || completed, 'pt-24': $root.aspect === 'lg' }">

            <p v-if="$root.aspect !== 'sm'" class="fw-b text-dark">{{ $moment(date).add(0, 'day').format("dddd") }}</p>
            <p class="small text-dark mb-16">{{ $moment(date).add(0, 'day').format("D MMMM") }}</p>

            <div class="d-flex flex-wrap justify-content-center" :class="{'mb-16': $root.aspect === 'sm', 'mb-60': $root.aspect === 'lg'}">

                <div class="d-flex flex-row w-100">
                    <div class="d-flex flex-column mx-16 w-100 small" :class="{'mb-16': $root.aspect === 'sm', 'mb-32': $root.aspect === 'lg'}" :style="{ opacity: data.in_bed ? 1 : 0.3 }">
                        <i-to-bed class="i-40 mx-auto mb-16 icon" :class="sleepStatus"></i-to-bed>
                        <template v-if="data.in_bed">{{ [data.in_bed, serverDateFormat ] | moment("HHumm") }}</template>
                        <template v-else-if="recommendedInBed">{{ [ recommendedInBed, "HH:mm" ] | moment("HHumm") }}</template>
                    </div>

                    <div class="d-flex flex-column mx-16 w-100 small" :class="{'mb-16': $root.aspect === 'sm', 'mb-32': $root.aspect === 'lg'}" :style="{ opacity: data.out_bed ? 1 : 0.3 }">
                        <i-get-out class="i-40 mx-auto mb-16 icon" :class="wakeupStatus"></i-get-out>
                        <template v-if="data.out_bed">{{ [ data.out_bed, serverDateFormat ] | moment("HHumm") }}</template>
                        <template v-else-if="recommendedWakup">{{ [ recommendedWakup, "HH:mm" ] | moment("HHumm") }}</template>
                    </div>
                </div>

                <div class="d-flex flex-row w-100" v-if="(!isAfter && !isYesterday && completed) || completed">
                    <div class="d-flex flex-column mx-16 w-100 small" :class="{'mb-16': $root.aspect === 'sm', 'mb-32': $root.aspect === 'lg'}">
                        <i-clock class="i-40 mx-auto mb-16 icon" :class="inBedTimeStatus"></i-clock>
                        <p class="item-text text-center w-100">{{  [ tib, "HH:mm" ] | moment("HHumm")}}</p>
                        <p class="small text-center w-100">Totale bedtijd</p>
                    </div>

                    <div class="d-flex flex-column mx-16 w-100 small" :class="{'mb-16': $root.aspect === 'sm', 'mb-32': $root.aspect === 'lg'}">
                        <i-clock class="i-40 mx-auto mb-16 icon" :class="awakeTimeStatus"></i-clock>
                        <p class="item-text text-center w-100">{{  [ tst, "HH:mm" ] | moment("HHumm")}}</p>
                        <p class="small text-center w-100">Totale slaapduur</p>
                    </div>
                </div>
            </div>
            <div class="d-flex flex-wrap justify-content-center">
                <template v-if="!disabled">
                    <router-link v-if="(!isAfter && !isYesterday && !locked && completed) || completed" :to="{ name: 'schedule-edit' , params: { id: data.id }, query: { week: week, date: data.date }}" class="small text-primary-dark text-center mb-16">
                        {{ $t('pages.schedule.change_schedule') }}
                    </router-link>
                </template>
                <router-link :to="{ name: 'schedule-details', query: { date: data.date, week: week } }" v-if="(isYesterday && !locked && !completed ) || (!completed && !isAfter && !locked)" class="btn btn-primary-dark w-100 text-center" :class="{ urgent: disabled }">
                    <span v-if="disabled" v-show="showNote" class="urgent-note"></span>
                    <span v-if="disabled" class="urgent-marker">!</span>
                    {{ $t('pages.schedule.fill_in') }}
                </router-link>
                <router-link :to="{ name: 'tip-detail', params: { id: data.day }, query: { date: data.date } }" v-else-if="completed" class="btn btn-outline-gold w-100 text-center">
                    {{ $t('pages.schedule.advice') }}
                </router-link>
            </div>

        </div>
    </div>
</template>

<script>
export default {
    props: {
        data: {
            required: true,
        },
        recommendedInBed: {
            default: ''
        },
        recommendedWakup: {
            default: ''
        },
        week: {
            required: true
        },
        day: {
            required: true
        },
        disabled: {
            default: false
        },
        locked: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        sleep: null,
        wakeup: null,
        date: null,

        serverDateFormat: "YYYY-MM-DD HH:mm:ss",

        showNote: false,
    }),
    mounted() {
        this.date = this.$moment(this.data.date, 'YYYY-MM-DD')
    },
    computed: {
        completed() {
            return !!this.data.id
        },
        isYesterday() {
            if (!this.date) return false
            return this.date.isSame(this.$moment().subtract(1, 'd').startOf('day'), 'd')
        },
        isTomorrow() {
            if (!this.date) return false
            return this.date.isSame(this.$moment().add(1, 'd').startOf('day'), 'd')
        },
        isToday() {
            if (!this.date) return false
            return this.date.isSame(this.$moment().startOf('day'), 'd')
        },
        isAfter() {
            if (!this.date) return false
            return this.date.startOf('day').add(5, 'h').isAfter(this.$moment())
        },
        tib() {
            return this.$moment().startOf('day').add(this.$moment.duration(this.$moment(this.data.out_bed, this.serverDateFormat).diff(this.$moment(this.data.in_bed, this.serverDateFormat)))).format('HH:mm')
        },
        tst() {
            return this.data.sleep_time
        },

        sleepStatus() {
            if (this.week === 1) return 'medium'
            if (!this.recommendedWakup || !this.recommendedInBed || !this.data.asleep) return 'medium'

            let date_inBed = this.$moment(this.data.in_bed, this.serverDateFormat)
            let date_outBed = this.$moment(this.data.out_bed, this.serverDateFormat)
            let date_asleep = this.$moment(this.data.asleep, this.serverDateFormat)
            let date_awake = this.$moment(this.data.awake, this.serverDateFormat)
            let rec_in_bed = this.$moment(this.data.date + ' ' + this.recommendedInBed, this.serverDateFormat)

            if (rec_in_bed.hour() > 17 && date_inBed.hour() <= 17)
                date_inBed.add(1, 'days')

            if (rec_in_bed.hour() <= 17 && date_inBed.hour() > 17)
                date_inBed.add(-1, 'days')

            if (date_inBed.isAfter(date_outBed))
                date_outBed.add(1, 'days')

            if (date_inBed.isAfter(date_asleep) || date_inBed.isSame(date_asleep))
                date_asleep.add(1, 'days')

            if (date_inBed.isAfter(date_awake))
                date_awake.add(1, 'days')

            if (date_inBed.isAfter(rec_in_bed) || date_inBed.isSame(rec_in_bed)) return 'good'

            return 'bad'
        },
        wakeupStatus() {
            if (!this.data.id || this.week === 1 || !this.recommendedWakup) return 'medium'

            let awakenMoment = this.$moment(this.data.out_bed, this.serverDateFormat)
            let recAwakenBedMoment = this.$moment(this.data.date + ' ' + this.recommendedWakup + ":00", this.serverDateFormat)

            if (!awakenMoment.isSame(recAwakenBedMoment, 'd'))
                recAwakenBedMoment.add(1, 'd')

            if (awakenMoment.isBefore(recAwakenBedMoment)) return 'good'

            let timeDiff = this.$moment.duration(awakenMoment.diff(recAwakenBedMoment)).asMinutes()

            if (timeDiff === 0) return 'good'

            else return 'bad'
        },

        inBedTimeStatus() {
            if (this.week === 1) return 'medium'
            if (!this.recommendedWakup || !this.recommendedInBed) return 'medium'

            let date_inBed = this.$moment(this.data.in_bed, this.serverDateFormat)
            let date_outBed = this.$moment(this.data.out_bed, this.serverDateFormat)
            let date_asleep = this.$moment(this.data.asleep, this.serverDateFormat)
            let date_awake = this.$moment(this.data.awake, this.serverDateFormat)
            let rec_in_bed = this.$moment(this.data.date + ' ' + this.recommendedInBed, this.serverDateFormat)

            if (rec_in_bed.hour() > 17 && date_inBed.hour() <= 17)
                date_inBed.add(1, 'days')

            if (rec_in_bed.hour() <= 17 && date_inBed.hour() > 17)
                date_inBed.add(-1, 'days')

            if (date_inBed.isAfter(date_outBed))
                date_outBed.add(1, 'days')

            if (date_inBed.isAfter(date_asleep) || date_inBed.isSame(date_asleep))
                date_asleep.add(1, 'days')

            if (date_inBed.isAfter(date_awake))
                date_awake.add(1, 'days')

            if (date_inBed.isAfter(rec_in_bed) || date_inBed.isSame(rec_in_bed)) return 'good'

            return 'bad'
        },

        awakeTimeStatus() {
            if (this.week === 1) return 'medium'
            if (!this.recommendedWakup || !this.recommendedInBed) return 'medium'

            let tst = this.$moment.duration(this.data.sleep_time).asMinutes()
            let rec_tst = this.$moment.duration(this.$moment(this.recommendedWakup, 'HH:mm').diff(this.$moment(this.recommendedInBed, 'HH:mm'))).asMinutes()
            if (rec_tst < 0)
                rec_tst = this.$moment.duration(this.$moment(this.recommendedInBed, 'HH:mm').diff(this.$moment(this.recommendedWakup, 'HH:mm'))).asMinutes()

            if (tst <= rec_tst) return 'good'

            if ((tst > rec_tst) && (tst <= rec_tst + 15)) return 'medium'

            return 'bad'
        }
    }
}
</script>
